import { Link } from "react-router-dom";
import { ChevronRight, MessageSquare, Pencil } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";


export function MemberCard({ m, onReminder, sending, showReminder, onEdit, onWhatsApp }) {
    // Card background telegraphs the profile status — no text chip needed.
    const cardBg = m.submitted
        ? "bg-emerald-50 border-emerald-200"
        : "bg-amber-50/50 border-amber-200/60";
    return (
        <div
            className={`group rounded-xl border p-5 hover:-translate-y-1 hover:shadow-md ${cardBg}`}
            style={{ transition: "transform 220ms ease, box-shadow 220ms ease" }}
            data-testid={`member-card-${m.token}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <Link
                        to={`/admin/members/${m.token}`}
                        className="rotary-heading text-lg text-rotary-ink hover:text-rotary-blue"
                        data-testid={`member-name-link-${m.token}`}
                    >
                        {m.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-muted-foreground">{m.phone}</p>
                    {m.club_name && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            <span className="text-rotary-blue">{m.role_display || m.position || "Member"}</span> · {m.club_name}
                        </p>
                    )}
                </div>
                {m.submitted && (
                    <span
                        className="rotary-chip bg-emerald-500 text-white border-transparent"
                        data-testid={`member-status-${m.token}`}
                    >
                        Filled
                    </span>
                )}
            </div>

            <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-between text-[11px]">
                    <span className="label-eyebrow">Profile</span>
                    <span className="font-semibold text-rotary-ink">{m.progress}%</span>
                </div>
                <Progress
                    value={m.progress}
                    className="h-2 bg-muted [&>div]:rotary-progress-fill"
                    data-testid={`member-progress-${m.token}`}
                />
            </div>

            <div className="mt-5 flex items-center gap-2">
                <Link to={`/admin/members/${m.token}`} className="flex-1">
                    <Button
                        variant="outline"
                        className="w-full rounded-full border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                        data-testid={`view-member-btn-${m.token}`}
                    >
                        View <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                </Link>
                <Button
                    variant="outline"
                    onClick={() => onEdit?.(m)}
                    className="rounded-full h-9 w-9 p-0 border-rotary-blue text-rotary-blue hover:bg-rotary-blue hover:text-white"
                    data-testid={`edit-member-btn-${m.token}`}
                    title="Quick edit"
                    aria-label={`Edit ${m.name}`}
                >
                    <Pencil className="h-4 w-4" />
                </Button>
                {showReminder && (
                    <>
                        <Button
                            onClick={() => onReminder(m)}
                            disabled={sending}
                            className="rounded-full bg-rotary-gold text-rotary-ink hover:bg-rotary-gold/85"
                            data-testid={`send-reminder-btn-${m.token}`}
                            title="Send SMS reminder"
                        >
                            <MessageSquare className="mr-1.5 h-4 w-4" />
                            {sending ? "Sending…" : "SMS"}
                        </Button>
                        <Button
                            onClick={() => onWhatsApp?.(m)}
                            className="inline-flex items-center justify-center rounded-full bg-[#25D366] text-white px-3.5 h-9 text-sm font-medium hover:bg-[#1ebe57]"
                            data-testid={`send-whatsapp-btn-${m.token}`}
                            title="Compose & send WhatsApp"
                            aria-label={`Send WhatsApp to ${m.name}`}
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
